import { Component, Vue, Watch } from 'vue-property-decorator';
import { isEqual } from 'lodash';

import AnalysisStep from '@/components/analysis/steps/AnalysisStep.vue';
import AnalysisStepConditionsPainsDialog from '@/components/analysis/steps/conditions/AnalysisStepConditionsPainsDialog.vue';
import AnalysisStepConditionsShoesTypeDialog from '@/components/analysis/steps/conditions/AnalysisStepConditionsShoesTypeDialog.vue';
import AnalysisStepConditionsWalkingAidsDialog from '@/components/analysis/steps/conditions/AnalysisStepConditionsWalkingAidsDialog.vue';
import PatientPathologyAddOrEdit from '@/components/patients/PatientPathologyAddOrEdit.vue';
import { StepsKeys } from '@/plugins/podolib/services/analysis';

// ---------------------------------------------------------------------------------------------------------------------

export enum ConditionsPainLocations {
  Hip = 'hip',
  Thigh = 'thigh',
  Knee = 'knee',
  Leg = 'leg',
  Ankle = 'ankle',
  Foot = 'foot',
}

export enum ConditionsPainSide {
  Left = 'left',
  Right = 'right',
}

export enum ConditionsPainZone_1 {
  Medial = 'medial',
  Side = 'side',
  Both = 'both',
  NotConcerned = 'not-concerned',
}

export enum ConditionsPainZone_2_C1 {
  Anterior = 'anterior',
  Posterior = 'posterior',
  Both = 'both',
  NotConcerned = 'not-concerned',
}

export enum ConditionsPainZone_2_C2 {
  Plantar = 'plantar',
  Dorsal = 'dorsal',
  Both = 'both',
  NotConcerned = 'not-concerned',
}

export type ConditionsPainLocations_c1 = Exclude<ConditionsPainLocations, ConditionsPainLocations.Foot>;
export type ConditionsPainLocations_c2 = ConditionsPainLocations.Foot;

export type ConditionsPain_f1 = {
  side: ConditionsPainSide;
  zone_1: ConditionsPainZone_1;
  level: number;
};

export type ConditionsPain =
  | (ConditionsPain_f1 & {
      location: ConditionsPainLocations_c1;
      zone_2: ConditionsPainZone_2_C1;
    })
  | (ConditionsPain_f1 & {
      location: ConditionsPainLocations_c2;
      zone_2: ConditionsPainZone_2_C2;
    });

// ---------------------------------------------------------------------------------------------------------------------

export enum ConditionsShoesTypeRigidity {
  Low = 'low',
  Moderate = 'moderate',
  Important = 'important',
}

export enum ConditionsShoesTypeCushioning {
  Low = 'low',
  Moderate = 'moderate',
  Important = 'important',
}

export enum ConditionsShoesTypeWear {
  PosteroExternal = 'postero-external',
  PosteroMedian = 'postero-median',
  PosteroInternal = 'postero-internal',
  AnteroExternal = 'antero-external',
  AnteroMedian = 'antero-median',
  AnteroInternal = 'antero-internal',
}

export type ConditionsShoesType = {
  rigidity: ConditionsShoesTypeRigidity;
  cushioning: ConditionsShoesTypeCushioning;
  wear: ConditionsShoesTypeWear;
};

// ---------------------------------------------------------------------------------------------------------------------

export enum ConditionsWalkingAidDevices {
  SimpleCane = 'simple-cane',
  EnglishCane = 'english-cane',
  EnglishCane2 = 'english-cane-2',
  Rollator = 'rollator',
  Walker = 'walker',
  LiftAndOrthopaedicShoes = 'lift-and-orthopaedic-shoes',
  Other = 'other',
}

// ---------------------------------------------------------------------------------------------------------------------

export type StepConditions = {
  pains: ConditionsPain[];
  shoes_type: ConditionsShoesType | null;
  walking_aids: ConditionsWalkingAidDevices[];
  pathologies: any | null;
};

@Component({
  components: {
    AnalysisStep,
    AnalysisStepConditionsPainsDialog,
    AnalysisStepConditionsShoesTypeDialog,
    AnalysisStepConditionsWalkingAidsDialog,
    PatientPathologyAddOrEdit,
  },
})
export default class AnalysisStepConditions extends Vue {
  // Main data payload
  data: StepConditions = null!;
  healthyPatient: boolean = true;

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Get manifest entry
   */
  get stepConditions() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.Conditions);
  }

  /**
   * Get 'patient' entity
   */
  get patient() {
    return this.$pdc.analysis!.entityPatient;
  }

  /**
   * Keys for translation
   */
  get shoesType() {
    return {
      data: this.data.shoes_type,
      key: 'analysis.steps.conditions.shoes_type',
    };
  }
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Keep manifest up to date
   */
  @Watch('data', { deep: true })
  onDataChanged(newValue: StepConditions) {
    if (!isEqual(newValue, this.stepConditions)) {
      this.$pdc.analysis!.setManifestEntry(StepsKeys.Conditions, this.data);
    }
  }

  /**
   * Keep local data up to date
   */
  @Watch('stepStation', { deep: true, immediate: true })
  onStepComputeChanged(newValue: StepConditions) {
    if (!isEqual(newValue, this.data)) {
      this.data = { ...this.data, ...this.stepConditions };
    }
  }

  // -------------------------------------------------------------------------------------------------------------------

  openPainsDialog(mode: 'add' | 'edit', index?: number) {
    (this.$refs.pains as any)._open(mode, { index });
  }

  removePain(index: number) {
    this.data.pains.splice(index, 1);
  }

  openShoesTypeDialog(mode: 'add' | 'edit') {
    (this.$refs.shoes_type as any)._open(mode);
  }

  removeShoesType() {
    this.data.shoes_type = null;
  }

  openWalkingAidsDialog(mode: 'add' | 'edit', index?: number) {
    (this.$refs.walking_aids as any)._open(mode, index);
  }

  removeWalkingAid(index: number) {
    this.data.walking_aids.splice(index, 1);
  }

  openPathologyDialog(mode: 'add' | 'edit', index?: number) {
    mode === 'add'
      ? this.$pdc.bus!.emit('pathology:add', false)
      : this.$pdc.bus!.emit('pathology:edit', this.data.pathologies[index!]);
  }

  removePathology(index: number) {
    if (this.data.pathologies[index].reference) {
      this.removePatientPathology(this.data.pathologies[index].reference._id, index);
    } else this.removePatientCustomPathology(this.data.pathologies[index]._id, index);
  }
  removePatientPathology(id: string, index: number) {
    try {
      this.$pdc.repository?.patchRessource({
        ressource: 'patients',
        params: {
          _id: this.patient!._id,
        },
        data: {
          $pull: {
            'infos.pathologies': {
              reference: id,
            },
          },
        },
      });
      this.data.pathologies.splice(index, 1);
    } catch (e) {
      console.log(e);
    }
  }
  removePatientCustomPathology(id: string, index: number) {
    try {
      this.$pdc.repository?.patchRessource({
        ressource: 'patients',
        params: {
          _id: this.patient!._id,
        },
        data: {
          $pull: {
            'infos.customPathologies': {
              _id: id,
            },
          },
        },
      });
      this.data.pathologies.splice(index, 1);
    } catch (e) {
      console.log(e);
    }
  }

  painChipColor(level: ConditionsPain['level']) {
    switch (true) {
      case level < 6:
        return 'warning';
      case level >= 6:
        return 'error';
      default:
        return 'grey';
    }
  }

  async findPathology(pathology: any) {
    const result = await this.$pdc
      .repository!.getRessource({
        ressource: 'pathologies',
        identifier: pathology.reference,
        params: {},
      })
      .catch((e: any) => {
        console.log(e);
      });
    this.data.pathologies.push(
      pathology.sinceDate
        ? {
            reference: result,
            sinceDate: pathology.sinceDate,
          }
        : {
            reference: result,
          },
    );
  }

  setSelectedPathology(data: any) {
    if (data.editedPathology)
      this.data.pathologies = this.data.pathologies.filter((value: any) => value !== data.editedPathology);
    if (data.pathology.name) {
      this.data.pathologies.push(data.pathology);
    } else {
      this.findPathology(data.pathology);
    }
  }

  mounted() {
    if (this.patient?.infos.pathologies?.length) this.data.pathologies.push(...this.patient.infos.pathologies);
    if (this.patient?.infos.customPathologies?.length)
      this.data.pathologies.push(...this.patient.infos.customPathologies);
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Emit "next" event to the parent
   */
  next() {
    this.$emit('next');
  }
}
